/**
 * E1's date filter, as data.
 *
 * 🔴 PURE, AND IN ITS OWN FILE, FOR THE REASON THIS FOLDER'S HEADER ALREADY
 * STATES: *"the repo has no DOM renderer (OD-60) and may not gain one, so a
 * decision made inside a component is a decision no test can reach."* A range
 * control is nothing but decisions — which preset is lit, what a half-typed
 * date means, what happens when someone picks an end before a start — so all of
 * them live here and `SchoolAdminScreens.tsx` only renders the answers.
 *
 * 🔴 IT MIRRORS `backend/src/modules/school/school-range.ts`, DELIBERATELY, AND
 * THE DUPLICATION IS THE POINT RATHER THAN AN ACCIDENT. The server is the
 * authority — it resolves whatever arrives and echoes `windowFrom`/`windowTo`
 * back, and every hint on the screen is drawn from that echo. What this copy
 * buys is that the INPUTS settle before the request rather than after it: pick
 * March-then-February and the boxes swap under your hand, instead of the page
 * loading a range the controls do not show. The rules are kept identical so the
 * two never disagree; if the server's ever change, this file changes with it.
 *
 * ⚠️ EVERYTHING HERE IS `YYYY-MM-DD` IN UTC, AND NEVER A `Date` IN LOCAL TIME.
 * `<input type="date">` speaks this format, the API speaks this format, and the
 * server buckets sittings by UTC day. Going through a local-time `Date` in
 * between is how a school in IST asks for the 15th at 02:00 and is shown the
 * 14th.
 */

const DAY_MS = 86_400_000

/** The windows the tab bar offers, in days. Ascending. */
export const RANGE_PRESETS = [7, 30, 90] as const

/** What E1 opens on — a half-term, matching the server's own default. */
export const DEFAULT_RANGE_DAYS = 30

/**
 * The server's ceiling, restated so the inputs stop before the round trip.
 *
 * ⚠️ IT IS A DENIAL-OF-SERVICE CONTROL, NOT A UI PREFERENCE, so this copy must
 * track `backend/.../school-range.ts` exactly — `GET /school/activity` reads
 * every sitting in the range unpaginated, and the cost scales with sittings
 * rather than with days. That file carries the full reasoning and the note on
 * what would let it be raised.
 */
export const MAX_RANGE_DAYS = 92

/** The earliest `from` that pairs with `to` without the server pulling it in. */
export function earliestFrom(to: string, now: Date = new Date()): string {
  const end = /^\d{4}-\d{2}-\d{2}$/.test(to.trim())
    ? Date.parse(`${to.trim()}T00:00:00.000Z`)
    : Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return new Date(end - (MAX_RANGE_DAYS - 1) * DAY_MS).toISOString().slice(0, 10);
}

export interface DateRange {
  /** `YYYY-MM-DD`, inclusive. */
  from: string
  /** `YYYY-MM-DD`, inclusive — the last day counted, not the day after. */
  to: string
}

/** `YYYY-MM-DD` → epoch ms at midnight UTC, or `null` if it is not a real day. */
function parseDay(day: string): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day.trim())
  if (!match) return null
  const year = Number(match[1])
  const month = Number(match[2])
  const date = Number(match[3])
  const ms = Date.UTC(year, month - 1, date)
  const back = new Date(ms)
  /* The round trip rejects 31 February, which `Date.UTC` would otherwise roll
     forward to 3 March without a word. Same check as the server's. */
  if (
    back.getUTCFullYear() !== year ||
    back.getUTCMonth() !== month - 1 ||
    back.getUTCDate() !== date
  ) {
    return null
  }
  return ms
}

/** Epoch ms → `YYYY-MM-DD`. */
function toDay(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10)
}

/** Today, in UTC, as `YYYY-MM-DD`. The upper bound on both date inputs. */
export function todayUtc(now: Date = new Date()): string {
  return now.toISOString().slice(0, 10)
}

/** Whether a string is a day this module can work with. */
export function isDay(day: string): boolean {
  return parseDay(day) !== null
}

/**
 * The last `days` days, ending today — what a preset tab means.
 *
 * ⚠️ INCLUSIVE OF BOTH ENDS, so "7 days" reaches back six, not seven. A window
 * that counted today plus seven previous days would be eight days wearing a
 * seven on its label, and the tile caption printed underneath it is a promise.
 */
export function presetRange(days: number, now: Date = new Date()): DateRange {
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
  const span = Math.min(Math.max(1, Math.round(days)), MAX_RANGE_DAYS)
  return { from: toDay(today - (span - 1) * DAY_MS), to: toDay(today) }
}

/** Days in a range, inclusive of both ends. `null` if either end is not a day. */
export function rangeDays(range: DateRange): number | null {
  const from = parseDay(range.from)
  const to = parseDay(range.to)
  if (from === null || to === null) return null
  return Math.round((to - from) / DAY_MS) + 1
}

/**
 * Which preset tab, if any, this range IS.
 *
 * 🔴 IT COMPARES THE DATES, NOT THE SPAN, AND THAT IS THE WHOLE DIFFERENCE.
 * "the 30 days ending today" and "the 30 days ending last month" are both 30
 * days; only the first is what the "30 days" tab means. Lighting the tab for
 * the second would tell a reader they are looking at this month while the page
 * shows last month.
 */
export function matchedPreset(range: DateRange, now: Date = new Date()): number | null {
  for (const days of RANGE_PRESETS) {
    const preset = presetRange(days, now)
    if (preset.from === range.from && preset.to === range.to) return days
  }
  return null
}

/**
 * Settle a range the reader has just edited.
 *
 * The same five rules the server applies, in the same order, so a range that
 * survives this is a range that comes back unchanged:
 *
 *   1. An end that is not a day is left exactly as typed — ⚠️ THE ONE RULE THAT
 *      IS NOT THE SERVER'S, AND IT IS ABOUT TYPING RATHER THAN ABOUT DATA. A
 *      person part-way through entering a year has a value this module cannot
 *      parse; rewriting it under them mid-keystroke is how a date input becomes
 *      unusable. The caller does not fetch until both ends parse.
 *   2. Backwards → swapped.
 *   3. Future → clamped to today.
 *   4. Longer than `MAX_RANGE_DAYS` → `from` moves forward, keeping the end the
 *      reader asked for.
 *   5. Whatever is left is a real range of at least one day.
 */
export function settleRange(range: DateRange, now: Date = new Date()): DateRange {
  const fromMs = parseDay(range.from)
  const toMs = parseDay(range.to)
  if (fromMs === null || toMs === null) return range

  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())

  let from = Math.min(fromMs, toMs)
  let to = Math.max(fromMs, toMs)

  if (to > today) to = today
  if (from > to) from = to

  const span = Math.round((to - from) / DAY_MS) + 1
  if (span > MAX_RANGE_DAYS) from = to - (MAX_RANGE_DAYS - 1) * DAY_MS

  return { from: toDay(from), to: toDay(to) }
}

/** Whether both ends parse — the gate on issuing a request. */
export function isCompleteRange(range: DateRange): boolean {
  return isDay(range.from) && isDay(range.to)
}
